import {DataStatus, Separator, showNotification} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useMemo} from 'react';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {GetWarehousesResponse, useGetDirectSaleQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  ArticleFolderTree,
  DmsDataGrid,
  WarehouseTabExternalFilters,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {suffixTestId, TestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {DS_SPARE_PARTS_GRID_CODE} from './DS_SPARE_PARTS_GRID_CODE';

function isStringRecord(value: unknown): value is Record<string, string> {
  return typeof value === 'object' && value !== null;
}

const DSMATERIAL_EXTERNAL_FILTER_ID = 'dsmaterial-external-filter';

interface WarehouseTabProps extends TestIdProps {
  onWarehouseDispatch: (warehouseArticleId: string) => Promise<void>;
  onWarehouseRequest: (warehouseArticleId: string) => Promise<boolean>;
  onWarehouseNonBinding: (warehouseArticleId: string) => Promise<boolean>;
  availableWarehouses: GetWarehousesResponse;
  onDataGridFiltersChange: (state: Record<string, string>) => void;
  hasPendingRequest: boolean;
}

export function RequestTabMaterialWarehouseTab(props: WarehouseTabProps) {
  const {directSaleId} = useWarehouseParams();
  const {
    data: directSale,
    isLoading,
    isError,
  } = useGetDirectSaleQuery({directSaleId: directSaleId ?? ''});

  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder({
    availableWarehouses: props.availableWarehouses,
  });

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  // Memoize action callback to prevent DataGrid from re-rendering unnecessarily
  const onWarehouseDispatch = props.onWarehouseDispatch;
  const onWarehouseRequest = props.onWarehouseRequest;
  const onWarehouseNonBinding = props.onWarehouseNonBinding;
  const warehouseActionCallback: ActionCallback = useCallback(
    ({actionKey, rowId, refreshData}) => {
      if (hasPendingRequestRef.current) {
        return showNotification.warning(
          i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
        );
      }

      const warehouseArticleId = isArray(rowId) ? head(rowId) : rowId;

      if (isNil(warehouseArticleId)) {
        return logError('Row id is not defined');
      }

      return match(actionKey)
        .with('dispatch', () => onWarehouseDispatch(warehouseArticleId).then(() => refreshData()))
        .with('request', () => onWarehouseRequest(warehouseArticleId).then(() => refreshData()))
        .with('nonBinding', () =>
          onWarehouseNonBinding(warehouseArticleId).then(() => refreshData())
        )
        .otherwise(() => catchUnhandledDataGridActions(actionKey));
    },
    [hasPendingRequestRef, onWarehouseDispatch, onWarehouseRequest, onWarehouseNonBinding]
  );

  // Memoize directSaleVariantId to prevent filterComponent from re-rendering
  const directSaleVariantId = useMemo(
    () => directSale?.directSaleVariantId,
    [directSale?.directSaleVariantId]
  );

  // Memoize filterComponent to prevent unnecessary DataGrid re-renders
  const filterComponent = useCallback(
    // eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
    (datagridProps: any) => (
      <WarehouseTabExternalFilters directSaleVariantId={directSaleVariantId} {...datagridProps} />
    ),
    [directSaleVariantId]
  );

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <HStack height="100%" data-testid={props['data-testid']}>
        <Box flex={1}>
          <VStack height="100%">
            <Box>
              <div id={DSMATERIAL_EXTERNAL_FILTER_ID}></div>
            </Box>
            <Space vertical={8} />
            <ArticleFolderTree
              ref={folderTreeRef}
              data-testid={suffixTestId('articleFolderTree', props)}
            />
          </VStack>
        </Box>

        <Separator orientation="vertical" />

        <Box flex={3}>
          <DmsDataGrid
            // DG must be re-rendered on folder change to update the query modifier
            // eslint-disable-next-line eslint-js/no-restricted-syntax
            key={dataGridModifier?.key}
            gridCode={DS_SPARE_PARTS_GRID_CODE}
            autoFocusSmartSearch
            actionCallback={warehouseActionCallback}
            externalFilterId={DSMATERIAL_EXTERNAL_FILTER_ID}
            onFilterChanged={(filters) => {
              if (isStringRecord(filters)) {
                props.onDataGridFiltersChange(filters);
              }
            }}
            filterComponent={filterComponent}
            queryModifier={dataGridModifier?.queryModifier}
            data-testid={DS_SPARE_PARTS_GRID_CODE}
          />
        </Box>
      </HStack>
    </DataStatus>
  );
}
